import React, { useState } from 'react'

import { ImageIcon, PlayIcon, XIcon } from 'lucide-react'

import { Alert, Box, IconButton, Stack, Typography, alpha, useTheme } from '@mui/material'

import { FieldLabel, Skeleton, fieldBorderColor, fieldFill } from '@repo/ui'

import { useSnackbar } from 'contexts'
import {
  REVEAL_POSTER_ACCEPT,
  REVEAL_POSTER_MAX_SIZE_MB,
  REVEAL_VIDEO_ACCEPT,
  REVEAL_VIDEO_MAX_SECONDS,
  REVEAL_VIDEO_MAX_SIZE_MB,
  uploadRevealPoster,
  uploadRevealVideo,
} from 'services/mediaApi'

// Direct path, NOT the `components` barrel: this file is re-exported by that barrel, so
// going through it would close a cycle (components → input → RevealVideoField →
// components) and resolve to undefined at module-init time — an empty render with no
// error pointing here. The same note ProductModelField, ScanMediaField and
// FileUploadField all carry.
import { FileUploadField } from './FileUploadField'

/**
 * Dropzone / preview height.
 *
 * 220, matching `ProductModelField` rather than `ScanMediaField`'s 140, and for its
 * reason: the reveal is the thing being JUDGED here. A brand is deciding whether this is
 * the moment they want a customer to see after tapping their product, which cannot be
 * told from a 140px thumbnail.
 */
const FIELD_HEIGHT = 220

/** The poster is one still beside the video, so it gets the smaller footprint. */
const POSTER_HEIGHT = 140

const VideoGlyph = ({ size, strokeWidth }: { size?: number; strokeWidth?: number }) => (
  <PlayIcon size={size} strokeWidth={strokeWidth} />
)

const PosterGlyph = ({ size, strokeWidth }: { size?: number; strokeWidth?: number }) => (
  <ImageIcon size={size} strokeWidth={strokeWidth} />
)

export interface RevealVideoFieldProps {
  /** The stored video URL, or null/undefined when nothing has been uploaded. */
  videoUrl?: string | null
  /** The stored poster URL. Required for the reveal to play — see the docblock. */
  posterUrl?: string | null
  /** Called with the next pair whenever either half changes. */
  onChange: (next: { videoUrl: string | null; posterUrl: string | null }) => void
  disabled?: boolean
}

/**
 * A product's REVEAL media: the short video played after one of its tags verifies, and
 * the poster frame that is the video's first paint.
 *
 * ## Why ONE field for two uploads
 *
 * They are two files and one setting. A video without a poster is not a usable reveal —
 * a full-screen `<video>` with no poster paints black until its first frame decodes,
 * which at the moment someone has just verified a physical object reads as a broken page
 * rather than as a beat. The server agrees (`resolveReveal` requires both), so a field
 * that let a brand upload one and walk away would be a field that silently produced
 * nothing. Keeping them together is what makes the incomplete state VISIBLE, which is
 * the whole job of the warning below.
 *
 * Two separate endpoints behind it, though, so replacing the poster does not mean
 * re-uploading the video — which for a reveal that opens on black is the difference
 * between a usable poster and a useless one.
 *
 * ## The limits are stated before upload, not after rejection
 *
 * Required by the acceptance criteria and worth the words: a brand hitting a cap after
 * waiting for a 40MB upload has learned the rule the expensive way. The DURATION is also
 * checked client-side here — `HTMLVideoElement.duration` after `loadedmetadata` — purely
 * to fail fast with a good message. That check is a COURTESY, not the gate: the number is
 * client-supplied, so the API re-reads it from the container's own header and its answer
 * is the one that counts. This is also why the server's measured duration is what gets
 * displayed afterwards rather than the browser's.
 */
export const RevealVideoField = ({
  videoUrl,
  posterUrl,
  onChange,
  disabled = false,
}: RevealVideoFieldProps) => {
  const theme = useTheme()
  /*
    ONE cast, for the field-chrome helpers below — the same workaround ProductModelField,
    ScanMediaField and FileUploadField all document: the pnpm store holds two
    @mui/material copies, so the `Theme` this file's `useTheme()` returns is nominally
    incompatible with the `Theme` in @repo/ui's helper signatures. Same object at
    runtime, different declaration file.
  */
  const fieldTheme = theme as Parameters<typeof fieldFill>[0]

  const { open: notification } = useSnackbar()

  const [isUploadingVideo, setIsUploadingVideo] = useState(false)
  const [isUploadingPoster, setIsUploadingPoster] = useState(false)
  const [measuredSeconds, setMeasuredSeconds] = useState<number | null>(null)
  /*
    Bumped to REMOUNT FileUploadField, which owns its own file list internally. Without
    it a rejected file sits in the dropzone afterwards, so the user is looking at a file
    the server never accepted and cannot retry the same one. One key per dropzone.
  */
  const [videoKey, setVideoKey] = useState(0)
  const [posterKey, setPosterKey] = useState(0)

  /**
   * Read the duration locally so an over-length clip is refused in under a second
   * instead of after a 5MB upload.
   *
   * Resolves `null` rather than rejecting when the browser cannot read the file — an
   * unreadable duration HERE must not block the upload, because the server is the gate
   * and it may well read a container this browser would not. Failing closed in the
   * client would refuse files the API would have accepted.
   */
  const readDurationLocally = (file: File): Promise<number | null> =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file)
      const probe = document.createElement('video')
      // Never load more than the header for this.
      probe.preload = 'metadata'
      probe.muted = true

      const done = (seconds: number | null) => {
        URL.revokeObjectURL(url)
        probe.removeAttribute('src')
        resolve(seconds)
      }

      probe.onloadedmetadata = () => {
        const { duration } = probe
        done(Number.isFinite(duration) && duration > 0 ? duration : null)
      }
      probe.onerror = () => done(null)
      // A file the browser stalls on must not hang the form. The server still decides.
      window.setTimeout(() => done(null), 4000)

      probe.src = url
    })

  const handleVideoFiles = async (files: File[]) => {
    // FileUploadField emits `[]` on mount, and again after the remount below. Neither is
    // a user clearing anything — the clear affordance is the X on the preview — so an
    // empty list is ignored rather than treated as a clear.
    const file = files[0]
    if (!file || isUploadingVideo) return

    setIsUploadingVideo(true)
    try {
      const localSeconds = await readDurationLocally(file)
      if (localSeconds !== null && localSeconds > REVEAL_VIDEO_MAX_SECONDS) {
        // Rounded up, so a 6.4s clip is not described as "6s" in a message explaining
        // that 6s is the limit.
        throw new Error(
          `That clip is ${Math.ceil(localSeconds)}s. A reveal can be up to ` +
            `${REVEAL_VIDEO_MAX_SECONDS}s, because it plays before the certificate. ` +
            `Trim it to the moment itself.`,
        )
      }

      const { path, duration_seconds } = await uploadRevealVideo(file)
      // The SERVER's measurement, not the browser's — see the docblock.
      setMeasuredSeconds(duration_seconds || localSeconds)
      onChange({ videoUrl: path, posterUrl: posterUrl ?? null })
    } catch (error: any) {
      notification({
        type: 'error',
        title: error?.message || "Couldn't upload that video",
      })
      // Previous value stays intact — a failed upload replaces nothing.
      setVideoKey((k) => k + 1)
    } finally {
      setIsUploadingVideo(false)
    }
  }

  const handlePosterFiles = async (files: File[]) => {
    const file = files[0]
    if (!file || isUploadingPoster) return

    setIsUploadingPoster(true)
    try {
      const { path } = await uploadRevealPoster(file)
      onChange({ videoUrl: videoUrl ?? null, posterUrl: path })
    } catch (error: any) {
      notification({
        type: 'error',
        title: error?.message || "Couldn't upload that image",
      })
      setPosterKey((k) => k + 1)
    } finally {
      setIsUploadingPoster(false)
    }
  }

  const clearVideo = () => {
    setMeasuredSeconds(null)
    // Clears BOTH halves, and that is deliberate rather than tidy-minded: a poster with
    // no video is not a partial reveal, it is an orphan nothing will ever render. Leaving
    // it would show a brand a configured-looking poster for a reveal that cannot play.
    onChange({ videoUrl: null, posterUrl: null })
    setVideoKey((k) => k + 1)
    setPosterKey((k) => k + 1)
  }

  const clearPoster = () => {
    onChange({ videoUrl: videoUrl ?? null, posterUrl: null })
    setPosterKey((k) => k + 1)
  }

  /** The state the warning exists for: media that looks configured and cannot play. */
  const needsPoster = Boolean(videoUrl) && !posterUrl && !isUploadingPoster

  return (
    // ONE element in the layout (§31): label, controls and helper live in a single
    // container, so a parent `<Stack spacing={n}>` separates FIELDS and never gets
    // between this label and its control.
    <Box sx={{ width: '100%' }}>
      <FieldLabel label="Reveal video" adornment="(optional)" />

      {/* §67 — the limits, stated while a brand is choosing a file rather than after
          they have waited for one to upload. Separate sentences, not em-dash-joined
          clauses, and lineHeight 1.5 so it does not read dense (§15). */}
      <Typography
        variant="body3"
        color="text.secondary"
        sx={{ lineHeight: 1.5, display: 'block', mb: 3 }}
      >
        MP4 or WebM, up to {REVEAL_VIDEO_MAX_SIZE_MB}MB and {REVEAL_VIDEO_MAX_SECONDS}{' '}
        seconds. It plays automatically for everyone who taps a tag, so keep it short.
        A .mov from a phone will not work in most browsers. Export as MP4 instead.
      </Typography>

      {videoUrl ? (
        <Box
          sx={{
            position: 'relative',
            height: FIELD_HEIGHT,
            width: '100%',
            overflow: 'hidden',
            borderRadius: 2,
            // Same rest border every other field carries (§31), from the shared helper
            // rather than a local token that would drift.
            border: `1px solid ${fieldBorderColor(fieldTheme)}`,
            backgroundColor: fieldFill(fieldTheme),
          }}
        >
          {/*
            Previewed with the SAME attributes the consumer player uses — muted,
            playsInline, autoPlay, loop — so "it looked right in the form" cannot
            diverge from what ships. `controls` is deliberately absent for the same
            reason: the customer gets no controls, so neither does this preview.

            `key` on the source URL forces a reload when the video is replaced; a
            <video> keeps its old source otherwise and the brand reviews the file they
            just overwrote.
          */}
          <video
            key={videoUrl}
            src={videoUrl}
            poster={posterUrl ?? undefined}
            muted
            playsInline
            autoPlay
            loop
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />

          {/* X, not a trash can (§38): this severs the association between the record
              and a stored file. Nothing is destroyed and it is cheap to undo by
              uploading again, so it takes no confirmation either. */}
          {!disabled && (
            <IconButton
              onClick={clearVideo}
              aria-label="Remove this reveal video"
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                backgroundColor: alpha(theme.palette.common.black, 0.45),
                color: theme.palette.common.white,
                '&:hover': { backgroundColor: alpha(theme.palette.common.black, 0.65) },
              }}
            >
              <XIcon size={18} strokeWidth={1} />
            </IconButton>
          )}

          {measuredSeconds !== null && (
            <Typography
              variant="body4"
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                px: 2,
                py: 1,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.common.black, 0.45),
                color: theme.palette.common.white,
              }}
            >
              {measuredSeconds.toFixed(1)}s
            </Typography>
          )}
        </Box>
      ) : isUploadingVideo ? (
        // §19 — footprint parity, not a spinner in an empty box: the placeholder occupies
        // the exact space the dropzone (and then the preview) occupies, so nothing shifts
        // when the upload lands.
        <Stack spacing={2}>
          <Skeleton variant="rounded" width="100%" height={FIELD_HEIGHT} />
          <Typography variant="body4" color="text.secondary">
            Uploading…
          </Typography>
        </Stack>
      ) : (
        <Box
          sx={{ ...(disabled && { opacity: 0.5, pointerEvents: 'none' }) }}
          aria-disabled={disabled || undefined}
        >
          <FileUploadField
            key={videoKey}
            acceptFiles={REVEAL_VIDEO_ACCEPT}
            icon={VideoGlyph}
            description="MP4 or WebM video"
            maxFiles={1}
            maxSizeMb={REVEAL_VIDEO_MAX_SIZE_MB}
            showMaxFileSize
            height={FIELD_HEIGHT}
            galleryTitle="Reveal video"
            onFilesChange={(files) => {
              // FileUploadField hands back its own wrapper ({id, file, …}), not native
              // Files — same unwrap as ProductModelField and ScanMediaField.
              void handleVideoFiles(
                (files as unknown as { file: File }[]).map((wrapper) => wrapper.file),
              )
            }}
          />
        </Box>
      )}

      {/* The poster half. Only once there is a video to need one — offering a poster
          dropzone on an empty reveal would ask for a frame of nothing. */}
      {(videoUrl || isUploadingVideo) && (
        <Box sx={{ mt: 5 }}>
          <FieldLabel label="Poster frame" />

          <Typography
            variant="body3"
            color="text.secondary"
            sx={{ lineHeight: 1.5, display: 'block', mb: 3 }}
          >
            The still shown while the video loads. Required. Without it the screen is
            black for a moment, which looks broken. Up to {REVEAL_POSTER_MAX_SIZE_MB}MB.
          </Typography>

          {needsPoster && (
            // A warning, not an error: nothing is broken yet and the save will succeed.
            // It names the CONSEQUENCE rather than the rule, because "required" alone
            // reads as bureaucracy for a field whose absence silently disables the
            // feature (§43's argument about naming the effect).
            <Alert severity="warning" sx={{ mb: 3 }}>
              Add a poster frame to turn this reveal on. Until then it will not play.
            </Alert>
          )}

          {posterUrl ? (
            <Box
              sx={{
                position: 'relative',
                height: POSTER_HEIGHT,
                width: '100%',
                overflow: 'hidden',
                borderRadius: 2,
                border: `1px solid ${fieldBorderColor(fieldTheme)}`,
                backgroundColor: fieldFill(fieldTheme),
              }}
            >
              <Box
                component="img"
                key={posterUrl}
                src={posterUrl}
                alt="Reveal poster frame"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
              {!disabled && (
                <IconButton
                  onClick={clearPoster}
                  aria-label="Remove this poster frame"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    backgroundColor: alpha(theme.palette.common.black, 0.45),
                    color: theme.palette.common.white,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.common.black, 0.65),
                    },
                  }}
                >
                  <XIcon size={18} strokeWidth={1} />
                </IconButton>
              )}
            </Box>
          ) : isUploadingPoster ? (
            <Skeleton variant="rounded" width="100%" height={POSTER_HEIGHT} />
          ) : (
            <Box
              sx={{ ...(disabled && { opacity: 0.5, pointerEvents: 'none' }) }}
              aria-disabled={disabled || undefined}
            >
              <FileUploadField
                key={posterKey}
                acceptFiles={REVEAL_POSTER_ACCEPT}
                icon={PosterGlyph}
                description="JPG, PNG or WebP"
                maxFiles={1}
                maxSizeMb={REVEAL_POSTER_MAX_SIZE_MB}
                showMaxFileSize
                height={POSTER_HEIGHT}
                galleryTitle="Poster frame"
                onFilesChange={(files) => {
                  void handlePosterFiles(
                    (files as unknown as { file: File }[]).map((wrapper) => wrapper.file),
                  )
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
